import React, { useCallback, useState } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DefaultDialogProps } from 'src/Libs/modals'
import { Formik } from 'formik'
import { TextField } from 'src/Libs/inputs/formik'
import useModals from 'src/Hooks/useModals'
import { Box } from '@mui/material'
import { useInstallationAccessInviteFormInitializer } from '../../hooks'

export interface InstallationAccessInviteForm {
  email: string
}

export interface InstallationAccessInviteDialogProps extends DefaultDialogProps {
  onConfirm: (email: string, onClose: () => void) => void
  isInvitingOwner?: boolean
}

const InstallationAccessInviteDialog: React.VFC<InstallationAccessInviteDialogProps> = ({
  place,
  onClose: onDialogClose,
  onConfirm,
  isInvitingOwner,
}) => {
  const { initialValues, validationSchema } = useInstallationAccessInviteFormInitializer()
  const { t } = useCustomTranslation()
  const { openConfirmationModal } = useModals()
  const [processing, setProcessing] = useState<boolean>(false)

  const handleFinalConfirm = useCallback(
    (email: string, onClose: () => void): void => {
      setProcessing(true)
      onClose()
      onConfirm(email, onDialogClose)
    },
    [onConfirm, onDialogClose]
  )

  const handleConfirm = useCallback(
    (values: InstallationAccessInviteForm) => {
      openConfirmationModal({
        title: t('installation.access-and-invitations.dialogs.invite-confirm.title'),
        text: t('installation.access-and-invitations.dialogs.invite-confirm.description', { item: values.email }),
        onConfirm: onClose => handleFinalConfirm(values.email, onClose),
        buttonText: [t('common.cancel'), t('common.invite-confirm')],
      })
    },
    [handleFinalConfirm, openConfirmationModal, t]
  )

  return (
    <Formik initialValues={initialValues} onSubmit={handleConfirm} validationSchema={validationSchema}>
      {({ isValid, dirty, handleSubmit }) => {
        return (
          <Dialog
            title={t(`installation.access-and-invitations.dialogs.${isInvitingOwner ? 'invite-owner' : 'invite'}.title`)}
            onClose={onDialogClose}
            place={place}
            maxWidth="sm"
            actions={{
              primary: {
                name: t('common.invite'),
                disabled: !isValid && !dirty,
                onClick: handleSubmit,
                isLoading: processing,
              },
              secondary: [
                {
                  name: t('common.cancel'),
                },
              ],
            }}
          >
            <Box sx={{ py: 1 }}>
              <form onSubmit={handleSubmit}>
                <TextField name={'email'} label={t('common.email')} />
              </form>
            </Box>
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default InstallationAccessInviteDialog
